import React, { useState } from "react";
import { FileText, X, Clock, Save, Calendar } from "lucide-react";
import { useEvolucao } from "../context/EvolucaoContext";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { usePendencias } from "../../pendencias/context/PendenciasContext";
import {
  TIPO_CONSULTA_LABELS,
  TIPOS_CONSULTA_CAMPO_UNICO,
  TipoConsulta,
} from "../../../types";

interface EvolucaoEdit {
  id: string;
  data: string;
  peso?: number;
  altura?: number;
  fc?: number;
  pa?: string;
  queixaPrincipal?: string;
  historiaDoencaAtual?: string;
  revisaoSistemas?: string;
  exameFisico?: string;
  hipotesesDiagnosticas?: string;
  conduta?: string;
  observacoes?: string;
  tipoConsulta?: TipoConsulta;
  evolucaoLivre?: string;
  pacienteId: string;
}

interface EditarEvolucaoProps {
  evolucao: EvolucaoEdit;
  pacienteNome: string;
  onClose: () => void;
  onSucesso?: () => void;
}

export default function EditarEvolucao({
  evolucao,
  pacienteNome,
  onClose,
  onSucesso,
}: EditarEvolucaoProps) {
  const { editarEvolucao } = useEvolucao();
  const { adicionarPendencias } = usePendencias();
  const [pendenciasTexto, setPendenciasTexto] = useState("");

  const dataObj = new Date(evolucao.data);
  const dataAtual = `${dataObj.getFullYear()}-${String(
    dataObj.getMonth() + 1
  ).padStart(2, "0")}-${String(dataObj.getDate()).padStart(2, "0")}`;
  const horaAtual = `${String(dataObj.getHours()).padStart(2, "0")}:${String(
    dataObj.getMinutes()
  ).padStart(2, "0")}`;

  const [formData, setFormData] = useState({
    data: dataAtual,
    hora: horaAtual,
    peso: evolucao.peso ? String(evolucao.peso) : "",
    altura: evolucao.altura ? String(evolucao.altura) : "",
    fc: evolucao.fc ? String(evolucao.fc) : "",
    pa: evolucao.pa || "",
    tipoConsulta: evolucao.tipoConsulta || "",
    evolucaoLivre: evolucao.evolucaoLivre || "",
    queixaPrincipal: evolucao.queixaPrincipal || "",
    historiaDoencaAtual: evolucao.historiaDoencaAtual || "",
    revisaoSistemas: evolucao.revisaoSistemas || "",
    exameFisico: evolucao.exameFisico || "",
    hipotesesDiagnosticas: evolucao.hipotesesDiagnosticas || "",
    conduta: evolucao.conduta || "",
    observacoes: evolucao.observacoes || "",
  });

  const [salvando, setSalvando] = useState(false);

  // B90: tipos "focados" usam campo único; demais usam o form estruturado
  const campoUnico = TIPOS_CONSULTA_CAMPO_UNICO.includes(
    formData.tipoConsulta as TipoConsulta
  );

  const handleChange = (campo: string, valor: string) => {
    setFormData((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleSalvar = async () => {
    if (!formData.tipoConsulta) {
      alert("Selecione o Tipo de Consulta");
      return;
    }
    if (campoUnico) {
      if (!formData.evolucaoLivre.trim()) {
        alert("Preencha o campo Evolução");
        return;
      }
    } else if (!formData.queixaPrincipal.trim()) {
      alert("Preencha pelo menos a Queixa Principal");
      return;
    }
    // 📅 B296 — data da consulta não pode ser futura (permite hoje/retroativo).
    if (ehDataFutura(formData.data)) {
      alert(msgDataFutura("Data da consulta"));
      return;
    }
    setSalvando(true);
    try {
      const { data, hora, ...dadosFormulario } = formData;
      await editarEvolucao(evolucao.id, {
        ...dadosFormulario,
        tipoConsulta: dadosFormulario.tipoConsulta as TipoConsulta,
        peso: dadosFormulario.peso
          ? parseFloat(dadosFormulario.peso)
          : undefined,
        altura: dadosFormulario.altura
          ? parseFloat(dadosFormulario.altura)
          : undefined,
        fc: dadosFormulario.fc ? parseFloat(dadosFormulario.fc) : undefined,
        data: `${data}T${hora}:00`,
      });

      // Salvar novas pendências se houver
      const linhasPendencias = pendenciasTexto
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (linhasPendencias.length > 0) {
        await adicionarPendencias(
          evolucao.pacienteId,
          pacienteNome,
          linhasPendencias,
          evolucao.id
        );
      }

      alert("Consulta atualizada com sucesso!");
      if (onSucesso) onSucesso();
      onClose();
    } catch (error) {
      alert("Erro ao salvar consulta");
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px",
    border: "2px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box",
    resize: "vertical",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
    fontFamily: theme.fonts.sans,
  };

  const sectionStyle: React.CSSProperties = { marginBottom: "24px" };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        overflow: "auto",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          background: "white",
          borderRadius: "16px",
          padding: "32px",
          boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#1f2937",
                margin: "0 0 8px 0",
                fontFamily: theme.fonts.sans,
              }}
            >
              <FileText size={16} style={{ marginRight: "8px" }} /> Editar
              Consulta
            </h2>
            <p
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: theme.primary[700],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              {pacienteNome}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#e5e7eb",
              color: "#374151",
              border: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            <X size={14} style={{ marginRight: "6px" }} /> Fechar
          </button>
        </div>

        <div
          style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}
        >
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                <Calendar size={14} style={{ marginRight: "6px" }} /> Data da
                Consulta
              </label>
              <input
                type="date"
                value={formData.data}
                min="1900-01-01"
                max={hojeISO()}
                onChange={(e) => handleChange("data", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>
                <Clock size={14} style={{ marginRight: "6px" }} /> Hora da
                Consulta
              </label>
              <input
                type="time"
                value={formData.hora}
                onChange={(e) => handleChange("hora", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>
          </div>
          {/* SINAIS VITAIS E ANTROPOMETRIA */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Peso (kg)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.peso}
                onChange={(e) => handleChange("peso", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 70.5"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Altura (cm)</label>
              <input
                type="number"
                min="0"
                value={formData.altura}
                onChange={(e) => handleChange("altura", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 170"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>FC (bpm)</label>
              <input
                type="number"
                min="0"
                value={formData.fc}
                onChange={(e) => handleChange("fc", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 72"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>PA (mmHg)</label>
              <input
                type="text"
                value={formData.pa}
                onChange={(e) => handleChange("pa", e.target.value)}
                style={{ ...inputStyle, resize: "none" }}
                placeholder="Ex: 120/80"
              />
            </div>
          </div>
          {/* TIPO DE CONSULTA (B90) */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              Tipo de Consulta <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              value={formData.tipoConsulta}
              onChange={(e) => handleChange("tipoConsulta", e.target.value)}
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              {(Object.keys(TIPO_CONSULTA_LABELS) as TipoConsulta[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_CONSULTA_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {campoUnico && (
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Evolução <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                value={formData.evolucaoLivre}
                onChange={(e) => handleChange("evolucaoLivre", e.target.value)}
                style={{ ...inputStyle, minHeight: "220px" }}
                placeholder="Descreva a evolução: queixas, achados do exame, conduta e orientações..."
              />
            </div>
          )}

          {!campoUnico && (
            <>
          <div style={sectionStyle}>
            <label style={labelStyle}>
              Queixa Principal <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              value={formData.queixaPrincipal}
              onChange={(e) => handleChange("queixaPrincipal", e.target.value)}
              style={{ ...inputStyle, minHeight: "80px" }}
              placeholder="Descreva o motivo principal da consulta..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>História da Doença Atual</label>
            <textarea
              value={formData.historiaDoencaAtual}
              onChange={(e) =>
                handleChange("historiaDoencaAtual", e.target.value)
              }
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Descreva a evolução dos sintomas..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Revisão de Sistemas</label>
            <textarea
              value={formData.revisaoSistemas}
              onChange={(e) => handleChange("revisaoSistemas", e.target.value)}
              style={{ ...inputStyle, minHeight: "100px" }}
              placeholder="Sintomas por sistemas orgânicos..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Exame Físico</label>
            <textarea
              value={formData.exameFisico}
              onChange={(e) => handleChange("exameFisico", e.target.value)}
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Achados do exame físico..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Hipóteses Diagnósticas / CID</label>
            <textarea
              value={formData.hipotesesDiagnosticas}
              onChange={(e) =>
                handleChange("hipotesesDiagnosticas", e.target.value)
              }
              style={{ ...inputStyle, minHeight: "80px" }}
              placeholder="Diagnósticos e CID-10..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Conduta</label>
            <textarea
              value={formData.conduta}
              onChange={(e) => handleChange("conduta", e.target.value)}
              style={{ ...inputStyle, minHeight: "120px" }}
              placeholder="Prescrições, solicitações, encaminhamentos..."
            />
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleChange("observacoes", e.target.value)}
              style={{ ...inputStyle, minHeight: "80px" }}
              placeholder="Observações adicionais..."
            />
          </div>
            </>
          )}
        </div>
        {/* PENDÊNCIAS */}
        <div
          style={{
            margin: "16px 0",
            padding: "16px",
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderLeft: "4px solid #f59e0b",
            borderRadius: "8px",
          }}
        >
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#92400e",
              marginBottom: "6px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Clock size={14} style={{ marginRight: "6px" }} /> Adicionar
            pendências
          </label>
          <p
            style={{
              fontSize: "12px",
              color: "#92400e",
              margin: "0 0 8px 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            Uma pendência por linha. Pendências anteriores não são removidas.
          </p>
          <textarea
            value={pendenciasTexto}
            onChange={(e) => setPendenciasTexto(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #f59e0b",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
              boxSizing: "border-box",
              minHeight: "80px",
              background: "white",
              resize: "vertical",
            }}
            placeholder={"Solicitar TC abdome\nColher novo MELD"}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={handleSalvar}
            disabled={salvando}
            style={{
              background: salvando ? "#9ca3af" : theme.primary[600],
              color: "white",
              border: "none",
              padding: "12px 32px",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "700",
              cursor: salvando ? "not-allowed" : "pointer",
              fontFamily: theme.fonts.sans,
            }}
          >
            {salvando ? (
              <>
                <Clock size={14} style={{ marginRight: "6px" }} /> Salvando...
              </>
            ) : (
              <>
                <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                Alterações
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
